import { useEffect, useState, useRef, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { tenantsApi } from '../api/tenants-api';
import { taxStatusApi, type TaxStatusCategory } from '../api/tax-status-api';
import { MapViewer } from '../map-engine';
import { MapControls } from '../map-engine/MapControls';
import { MapStyleSelector } from '../map-engine/MapStyleSelector';
import { ErrorToast } from '../projects/components/ErrorToast';
import { getErrorMessage } from '../utils/error-helpers';
import type { Map as MapLibreMap } from 'maplibre-gl';
import './client-form.css';

/**
 * Datos del formulario de cliente
 */
interface ClientFormData {
  name: string;
  slug: string;
  cuit: string; // CUIT o ID Fiscal
  taxStatus: string; // Condición tributaria
  address: string;
  phone: string;
  email: string; // Email de contacto
  responsable: string; // Persona responsable
  country: string; // País de América
  languages: string[]; // Idiomas disponibles para contacto
}

/**
 * Países de América (Norte, Centro y Sur)
 */
const AMERICAN_COUNTRIES = [
  'Argentina', 'Bolivia', 'Brasil', 'Chile', 'Colombia', 'Ecuador', 'Guyana',
  'Paraguay', 'Perú', 'Surinam', 'Uruguay', 'Venezuela', // Sudamérica
  'Costa Rica', 'El Salvador', 'Guatemala', 'Honduras', 'Nicaragua', 'Panamá', // Centroamérica
  'Belice', 'México', // Norte de Centroamérica
  'Canadá', 'Estados Unidos', // Norteamérica
  'Antigua y Barbuda', 'Bahamas', 'Barbados', 'Cuba', 'Dominica', 'Granada',
  'Haití', 'Jamaica', 'República Dominicana', 'San Cristóbal y Nieves',
  'Santa Lucía', 'San Vicente y las Granadinas', 'Trinidad y Tobago', // Caribe
];

/**
 * Idiomas disponibles para contacto
 */
const AVAILABLE_LANGUAGES = [
  { code: 'es', name: 'Español' },
  { code: 'pt', name: 'Portugués' },
  { code: 'en', name: 'Inglés' },
  { code: 'fr', name: 'Francés' },
];

/**
 * ClientForm - Formulario para crear/editar clientes (SuperAdmin)
 *
 * @remarks
 * Permite a los SuperAdmin crear nuevos clientes/municipios o editar existentes.
 * Incluye mapa interactivo que muestra los países del continente americano con:
 * - Hover effect: países se iluminan suavemente al pasar el mouse
 * - Selección permanente: país seleccionado en el dropdown se ilumina en el mapa
 * - Controles personalizados: MapControls (zoom, brújula, 2D/3D, estilos) con glassmorphism
 * - Selector de estilo: MapStyleSelector modal para cambiar entre 5 estilos de mapa
 *
 * **Cumple con:**
 * - P1: Componente < 600 líneas
 * - P3: Documentación JSDoc completa
 */
export function ClientForm(): JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<ClientFormData>({
    name: '',
    slug: '',
    cuit: '',
    taxStatus: '',
    address: '',
    phone: '',
    email: '',
    responsable: '',
    country: '',
    languages: [],
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSavingClient, setIsSavingClient] = useState(false);
  const [taxStatusCategories, setTaxStatusCategories] = useState<TaxStatusCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  // Validación de slug único
  const [slugValidation, setSlugValidation] = useState<'idle' | 'validating' | 'available' | 'taken'>('idle');
  const [originalSlug, setOriginalSlug] = useState<string>('');

  // Referencia para el mapa
  const mapRef = useRef<MapLibreMap | null>(null);

  /**
   * Carga datos del cliente si estamos en modo edición
   */
  useEffect(() => {
    if (isEditMode && id) {
      const loadClient = async () => {
        try {
          setIsLoading(true);
          const client = await tenantsApi.getById(id);

          setFormData({
            name: client.name,
            slug: client.slug,
            cuit: (client as any).contactInfo?.cuit || '',
            taxStatus: client.taxStatus || '',
            address: (client as any).contactInfo?.address || '',
            phone: (client as any).contactInfo?.phone || '',
            email: (client as any).contactInfo?.email || '',
            responsable: (client as any).contactInfo?.responsable || '',
            country: (client as any).contactInfo?.country || '',
            languages: (client as any).contactInfo?.languages || [],
          });

          // Guardar slug original para validación
          setOriginalSlug(client.slug);
        } catch (err) {
          console.error('Error cargando cliente:', err);
          setError(getErrorMessage(err, 'Error al cargar los datos del cliente'));
        } finally {
          setIsLoading(false);
        }
      };

      void loadClient();
    }
  }, [isEditMode, id]);

  /**
   * Carga las categorías de tax status
   */
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoadingCategories(true);
        const data = await taxStatusApi.list();
        setTaxStatusCategories(data);
      } catch (err) {
        console.error('Error cargando categorías de tax status:', err);
      } finally {
        setLoadingCategories(false);
      }
    };
    void loadCategories();
  }, []);

  /**
   * Valida que el slug sea único (con debounce)
   */
  useEffect(() => {
    // No validar si el slug está vacío o es inválido
    if (!formData.slug || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(formData.slug)) {
      setSlugValidation('idle');
      return;
    }

    // No validar si el slug no ha cambiado (modo edición)
    if (isEditMode && formData.slug === originalSlug) {
      setSlugValidation('idle');
      return;
    }

    // Debounce de 500ms
    const timer = setTimeout(() => {
      const validateSlug = async () => {
        try {
          setSlugValidation('validating');
          const allTenants = await tenantsApi.getAll();

          const slugExists = allTenants.some(tenant =>
            tenant.slug === formData.slug && tenant.id !== id
          );

          setSlugValidation(slugExists ? 'taken' : 'available');
        } catch (err) {
          console.error('Error validando slug:', err);
          setSlugValidation('idle');
        }
      };

      void validateSlug();
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.slug, isEditMode, originalSlug, id]);

  /**
   * Crea una nueva categoría de tax status
   */
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;

    try {
      const newCategory = await taxStatusApi.create({ name: newCategoryName.trim() });
      setTaxStatusCategories([...taxStatusCategories, newCategory]);
      setFormData(prev => ({ ...prev, taxStatus: newCategory.name }));
      setShowNewCategoryInput(false);
      setNewCategoryName('');
    } catch (err: any) {
      setError(getErrorMessage(err, 'Error creando categoría'));
    }
  };

  /**
   * Maneja el toggle de idiomas
   */
  const handleLanguageToggle = (langCode: string) => {
    setFormData(prev => {
      const isSelected = prev.languages.includes(langCode);
      return {
        ...prev,
        languages: isSelected
          ? prev.languages.filter(l => l !== langCode)
          : [...prev.languages, langCode],
      };
    });
  };

  /**
   * Mapeo de nombres de países en español a inglés (para compatibilidad con datos de mapa)
   */
  const COUNTRY_NAME_MAPPING: Record<string, string> = {
    'Argentina': 'Argentina',
    'Bolivia': 'Bolivia',
    'Brasil': 'Brazil',
    'Chile': 'Chile',
    'Colombia': 'Colombia',
    'Ecuador': 'Ecuador',
    'Guyana': 'Guyana',
    'Paraguay': 'Paraguay',
    'Perú': 'Peru',
    'Surinam': 'Suriname',
    'Uruguay': 'Uruguay',
    'Venezuela': 'Venezuela',
    'Costa Rica': 'Costa Rica',
    'El Salvador': 'El Salvador',
    'Guatemala': 'Guatemala',
    'Honduras': 'Honduras',
    'Nicaragua': 'Nicaragua',
    'Panamá': 'Panama',
    'Belice': 'Belize',
    'México': 'Mexico',
    'Canadá': 'Canada',
    'Estados Unidos': 'United States',
    'Antigua y Barbuda': 'Antigua and Barbuda',
    'Bahamas': 'Bahamas',
    'Barbados': 'Barbados',
    'Cuba': 'Cuba',
    'Dominica': 'Dominica',
    'Granada': 'Grenada',
    'Haití': 'Haiti',
    'Jamaica': 'Jamaica',
    'República Dominicana': 'Dominican Republic',
    'San Cristóbal y Nieves': 'Saint Kitts and Nevis',
    'Santa Lucía': 'Saint Lucia',
    'San Vicente y las Granadinas': 'Saint Vincent and the Grenadines',
    'Trinidad y Tobago': 'Trinidad and Tobago',
  };

  // Estado para tracking de país en hover
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);

  // Estados para controles de mapa
  const [showStyleSelector, setShowStyleSelector] = useState(false);
  const [_viewMode, setViewMode] = useState<'2D' | '3D'>('2D');

  /**
   * Maneja la carga del mapa
   */
  const handleMapLoad = (map: MapLibreMap) => {
    mapRef.current = map;

    // Agregar fuente de datos de países (Natural Earth Data)
    map.addSource('countries', {
      type: 'geojson',
      data: 'https://raw.githubusercontent.com/nvkelso/natural-earth-vector/master/geojson/ne_110m_admin_0_countries.geojson',
    });

    // Capa base invisible para capturar eventos (debe estar primero)
    map.addLayer({
      id: 'countries-base',
      type: 'fill',
      source: 'countries',
      paint: {
        'fill-color': 'transparent',
        'fill-opacity': 0,
      },
    });

    // Capa de relleno para hover (debajo de todo)
    map.addLayer({
      id: 'countries-fill-hover',
      type: 'fill',
      source: 'countries',
      paint: {
        'fill-color': '#2FA4B5',
        'fill-opacity': 0.2,
      },
      filter: ['==', 'NAME', ''], // Inicialmente ninguno
    });

    // Capa de relleno para país seleccionado (encima del hover)
    map.addLayer({
      id: 'countries-fill-selected',
      type: 'fill',
      source: 'countries',
      paint: {
        'fill-color': '#2FA4B5',
        'fill-opacity': 0.7, // Aumentado de 0.5 a 0.7 para mejor visibilidad
      },
      filter: ['==', 'NAME', ''], // Inicialmente ninguno
    });

    // Capa de borde para todos los países
    map.addLayer({
      id: 'countries-border',
      type: 'line',
      source: 'countries',
      paint: {
        'line-color': 'rgba(47, 164, 181, 0.3)',
        'line-width': 0.5,
      },
    });

    // Capa de borde exterior para país seleccionado (glow effect)
    map.addLayer({
      id: 'countries-border-selected-glow',
      type: 'line',
      source: 'countries',
      paint: {
        'line-color': '#2FA4B5',
        'line-width': 6,
        'line-blur': 4, // Efecto de difuminado para simular glow
        'line-opacity': 0.4,
      },
      filter: ['==', 'NAME', ''],
    });

    // Capa de borde para país seleccionado (línea sólida)
    map.addLayer({
      id: 'countries-border-selected',
      type: 'line',
      source: 'countries',
      paint: {
        'line-color': '#2FA4B5',
        'line-width': 3, // Aumentado de 2 a 3 para mejor visibilidad
        'line-opacity': 1,
      },
      filter: ['==', 'NAME', ''],
    });

    // Hover effect - escuchar eventos en la capa base
    map.on('mousemove', 'countries-base', (e) => {
      if (e.features && e.features.length > 0) {
        const feature = e.features[0];
        const countryName = feature.properties?.NAME || feature.properties?.name;

        if (countryName) {
          setHoveredCountry(countryName);
          map.getCanvas().style.cursor = 'pointer';
        }
      }
    });

    map.on('mouseleave', 'countries-base', () => {
      setHoveredCountry(null);
      map.getCanvas().style.cursor = '';
    });
  };

  /**
   * Actualiza el hover effect en el mapa
   */
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    // Solo actualizar si el layer existe
    if (map.getLayer('countries-fill-hover')) {
      map.setFilter('countries-fill-hover',
        hoveredCountry ? ['==', 'NAME', hoveredCountry] : ['==', 'NAME', '']
      );
    }
  }, [hoveredCountry]);

  /**
   * Sincroniza el país seleccionado con el mapa
   */
  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    const selectedCountryEnglish = COUNTRY_NAME_MAPPING[formData.country];

    // Verificar que las capas existen antes de actualizar
    if (!map.getLayer('countries-fill-selected') ||
        !map.getLayer('countries-border-selected') ||
        !map.getLayer('countries-border-selected-glow')) {
      return;
    }

    // Actualizar filtros para mostrar el país seleccionado
    if (selectedCountryEnglish) {
      map.setFilter('countries-fill-selected', ['==', 'NAME', selectedCountryEnglish]);
      map.setFilter('countries-border-selected', ['==', 'NAME', selectedCountryEnglish]);
      map.setFilter('countries-border-selected-glow', ['==', 'NAME', selectedCountryEnglish]);
    } else {
      // Limpiar selección
      map.setFilter('countries-fill-selected', ['==', 'NAME', '']);
      map.setFilter('countries-border-selected', ['==', 'NAME', '']);
      map.setFilter('countries-border-selected-glow', ['==', 'NAME', '']);
    }
  }, [formData.country]);

  /**
   * Genera slug automáticamente desde el nombre
   */
  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      slug: name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove accents
        .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
        .trim()
        .replace(/\s+/g, '-'), // Replace spaces with hyphens
    }));
  };

  /**
   * Maneja el submit del formulario
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validaciones
    if (!formData.name.trim()) {
      setError('El nombre del cliente es requerido');
      return;
    }

    if (!formData.slug.trim()) {
      setError('El slug es requerido');
      return;
    }

    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(formData.slug)) {
      setError('El slug debe estar en formato kebab-case (ej: bahia-blanca)');
      return;
    }

    if (slugValidation === 'taken') {
      setError('El slug ya está en uso. Por favor, elige otro identificador');
      return;
    }

    if (!formData.cuit.trim()) {
      setError('El CUIT es requerido');
      return;
    }

    if (!formData.taxStatus) {
      setError('La condición tributaria es requerida');
      return;
    }

    if (!formData.responsable.trim()) {
      setError('El responsable es requerido');
      return;
    }

    if (!formData.phone.trim()) {
      setError('El teléfono es requerido');
      return;
    }

    if (!formData.address.trim()) {
      setError('La dirección es requerida');
      return;
    }

    if (!formData.country) {
      setError('El país es requerido');
      return;
    }

    if (formData.languages.length === 0) {
      setError('Debe seleccionar al menos un idioma de contacto');
      return;
    }

    try {
      setIsSavingClient(true);

      // Construir contactInfo solo con campos que tienen valor
      const contactInfo: Record<string, unknown> = {};
      if (formData.cuit?.trim()) contactInfo.cuit = formData.cuit.trim();
      if (formData.address?.trim()) contactInfo.address = formData.address.trim();
      if (formData.phone?.trim()) contactInfo.phone = formData.phone.trim();
      if (formData.responsable?.trim()) contactInfo.responsable = formData.responsable.trim();
      if (formData.country) contactInfo.country = formData.country;
      if (formData.languages.length > 0) contactInfo.languages = formData.languages;
      if (formData.email?.trim()) contactInfo.email = formData.email.trim();

      const clientData: Record<string, unknown> = {
        name: formData.name,
        slug: formData.slug,
        contactInfo,
      };
      if (formData.taxStatus) clientData.taxStatus = formData.taxStatus;

      console.log('📤 Enviando datos del cliente:', clientData);

      if (isEditMode && id) {
        await tenantsApi.update(id, clientData);
        console.log('✅ Cliente actualizado exitosamente');
        navigate('/admin/dashboard?tab=clients');
      } else {
        await tenantsApi.create(clientData);
        console.log('✅ Cliente creado exitosamente');
        navigate('/admin/dashboard?tab=clients');
      }
    } catch (err: any) {
      console.error('❌ Error al guardar cliente:', err);
      console.error('❌ Response data:', err.response?.data);
      setError(getErrorMessage(err, 'Error al guardar el cliente'));
    } finally{
      setIsSavingClient(false);
    }
  };

  if (isLoading) {
    return (
      <div className="client-form-container">
        <div className="client-form-map-background" />
        <div className="client-form">
          <div className="loading-spinner">Cargando...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="client-form-container">
      {/* Toast de error emergente (centro superior) */}
      <ErrorToast
        message={error}
        onClose={() => setError(null)}
      />

      {/* Mapa de fondo con países del continente americano */}
      <div className="client-form-map-background">
        <MapViewer
          onMapLoad={handleMapLoad}
          showControls={false}
          showScale={true}
        />

        {/* Controles personalizados de mapa (glassmorphism WESTEER) */}
        <MapControls
          map={mapRef.current}
          onViewModeChange={setViewMode}
          onStyleChange={() => setShowStyleSelector(true)}
        />

        {/* Selector de estilo de mapa */}
        <MapStyleSelector
          map={mapRef.current}
          isOpen={showStyleSelector}
          onClose={() => setShowStyleSelector(false)}
        />
      </div>

      {/* Sidebar glassmorphic */}
      <form onSubmit={handleSubmit} className="client-form">
        {/* Header con logo y título */}
        <div className="client-form-header">
          <div className="client-form-logo-container">
            <img
              src="/images/westeer-logo-white.png"
              alt="WESTEER"
              className="client-form-logo"
            />
          </div>
          <div className="client-form-title-section">
            <h1 className="client-form-title">
              {isEditMode ? 'Editar Cliente' : 'Crear Nuevo Cliente'}
            </h1>
            <p className="client-form-subtitle">
              {isEditMode
                ? 'Modifica la información del cliente'
                : 'Completa los datos del nuevo cliente / municipio'}
            </p>
          </div>
        </div>

        {/* Contenido scrolleable */}
        <div className="client-form-content">
        <div className="form-section">
          <h2 className="section-title">Información Básica</h2>

          <div className="form-group">
            <label htmlFor="name">
              Nombre del Cliente <span className="required">*</span>
            </label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              required
              disabled={isSavingClient}
              placeholder="ej: Municipio de San Juan"
              className="form-input"
            />
            <small className="form-help">
              Nombre oficial del municipio o gobierno
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="slug">
              Identificador (Slug) <span className="required">*</span>
            </label>
            <input
              id="slug"
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
              required
              disabled={isSavingClient}
              placeholder="ej: bahia-blanca"
              pattern="^[a-z0-9]+(?:-[a-z0-9]+)*$"
              className="form-input"
              style={{
                borderColor:
                  slugValidation === 'taken'
                    ? 'var(--error)'
                    : slugValidation === 'available'
                    ? 'var(--success)'
                    : undefined,
              }}
            />
            <small
              className="form-help"
              style={{
                color:
                  slugValidation === 'taken'
                    ? 'var(--error)'
                    : slugValidation === 'available'
                    ? 'var(--success)'
                    : 'var(--text-secondary)',
              }}
            >
              {slugValidation === 'validating' && '⏳ Verificando disponibilidad...'}
              {slugValidation === 'taken' && '❌ Este slug ya está en uso'}
              {slugValidation === 'available' && '✅ Slug disponible'}
              {slugValidation === 'idle' &&
                'Solo letras minúsculas, números y guiones (generado automáticamente)'}
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="country">
              País <span className="required">*</span>
            </label>
            <select
              id="country"
              value={formData.country}
              onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
              required
              disabled={isSavingClient}
              className="form-select"
            >
              <option value="">Seleccionar país</option>
              {AMERICAN_COUNTRIES.map(country => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
            <small className="form-help">
              País donde se encuentra ubicado el cliente. El país seleccionado se iluminará en el mapa de fondo.
            </small>
          </div>

        </div>

        <div className="form-section">
          <h2 className="section-title">Información Fiscal</h2>

          <div className="form-group">
            <label htmlFor="cuit">
              CUIT / ID Fiscal <span className="required">*</span>
            </label>
            <input
              id="cuit"
              type="text"
              value={formData.cuit}
              onChange={(e) => setFormData(prev => ({ ...prev, cuit: e.target.value }))}
              required
              disabled={isSavingClient}
              placeholder="20-12345678-9"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="taxStatus">
              Condición Tributaria / Tax Status <span className="required">*</span>
            </label>
            {!showNewCategoryInput ? (
              <>
                <select
                  id="taxStatus"
                  value={formData.taxStatus}
                  onChange={(e) => {
                    if (e.target.value === '__new__') {
                      setShowNewCategoryInput(true);
                      setFormData(prev => ({ ...prev, taxStatus: '' }));
                    } else {
                      setFormData(prev => ({ ...prev, taxStatus: e.target.value }));
                    }
                  }}
                  required
                  disabled={isSavingClient || loadingCategories}
                  className="form-select"
                >
                  <option value="">Seleccionar (requerido)</option>

                  {/* Agrupar por país */}
                  {['Argentina', 'Canadá', 'Estados Unidos', 'México'].map((country) => {
                    const countryCategories = taxStatusCategories.filter(
                      (cat) => cat.countryName === country
                    );
                    if (countryCategories.length === 0) return null;

                    return (
                      <optgroup key={country} label={country}>
                        {countryCategories.map((cat) => (
                          <option key={cat.id} value={cat.name}>
                            {cat.name}
                          </option>
                        ))}
                      </optgroup>
                    );
                  })}

                  {/* Categorías sin país */}
                  {taxStatusCategories.filter((cat) => !cat.countryName).length > 0 && (
                    <optgroup label="Otras">
                      {taxStatusCategories
                        .filter((cat) => !cat.countryName)
                        .map((cat) => (
                          <option key={cat.id} value={cat.name}>
                            {cat.name}
                          </option>
                        ))}
                    </optgroup>
                  )}

                  <option value="__new__" style={{ fontWeight: 'bold', borderTop: '1px solid #ccc' }}>
                    + Agregar nueva categoría
                  </option>
                </select>
                <small className="form-help">
                  Selecciona una categoría existente o crea una nueva
                </small>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="Nombre de la nueva categoría"
                    className="form-input"
                    style={{ flex: 1 }}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={handleCreateCategory}
                    disabled={!newCategoryName.trim()}
                    className="btn-primary"
                    style={{ padding: '0.5rem 1rem' }}
                  >
                    Crear
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewCategoryInput(false);
                      setNewCategoryName('');
                    }}
                    className="btn-secondary"
                    style={{ padding: '0.5rem 1rem' }}
                  >
                    Cancelar
                  </button>
                </div>
                <small className="form-help">
                  Esta categoría quedará disponible para todos los usuarios
                </small>
              </>
            )}
          </div>
        </div>

        <div className="form-section">
          <h2 className="section-title">Datos de Contacto</h2>

          <div className="form-group">
            <label htmlFor="responsable">
              Responsable / Contacto Principal <span className="required">*</span>
            </label>
            <input
              id="responsable"
              type="text"
              value={formData.responsable}
              onChange={(e) => setFormData(prev => ({ ...prev, responsable: e.target.value }))}
              required
              disabled={isSavingClient}
              placeholder="Nombre del responsable"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">
              Teléfono <span className="required">*</span>
            </label>
            <input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              required
              disabled={isSavingClient}
              placeholder="+54 291 123-4567"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">
              Email de Contacto
            </label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              disabled={isSavingClient}
              placeholder="contacto@municipio.gob.ar"
              className="form-input"
            />
            <small className="form-help">
              Email principal para comunicaciones oficiales
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="address">
              Dirección <span className="required">*</span>
            </label>
            <textarea
              id="address"
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              required
              disabled={isSavingClient}
              placeholder="Dirección oficial del municipio"
              className="form-textarea"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>
              Idiomas de Contacto <span className="required">*</span>
            </label>
            <div className="languages-checklist">
              {AVAILABLE_LANGUAGES.map((lang) => (
                <div key={lang.code} className="language-checkbox-item">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.languages.includes(lang.code)}
                      disabled={isSavingClient}
                      onChange={() => handleLanguageToggle(lang.code)}
                    />
                    <span className="language-name">{lang.name}</span>
                  </label>
                </div>
              ))}
            </div>
            <small className="form-help">
              Seleccione los idiomas disponibles para comunicación con este cliente
            </small>
          </div>
        </div>
        </div>

        {/* Footer con botones de acción */}
        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/admin/dashboard?tab=clients')}
            className="btn-secondary"
            disabled={isSavingClient}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={isSavingClient}
          >
            {isSavingClient ? 'Guardando...' : (isEditMode ? 'Guardar Cambios' : 'Crear Cliente')}
          </button>
        </div>
      </form>
    </div>
  );
}
